/** "Filters & intel" for the department inboxes (Dean, Oct 2026) — the same idea as the Applications Inbox dialog:
 *  when Autopilot runs, the confidence it needs, a switch per rule, which categories get an AI pre-reply, free-text guidance
 *  for the triage and for the drafts, the policy documents the AI must follow, and the AI triage category list + audit. */
import { useEffect, useRef, useState } from "react";
import { BookOpenText, Download, FileText, Loader2, Settings2, ShieldCheck, Sparkles, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScheduleEditor } from "@/components/AutopilotSchedule";
import { uploadInboxAttachment } from "@/lib/api";
import { CATEGORY_TONE, flagTone } from "@/pages/inbox/CasePicker";
import { cn } from "@/lib/utils";

const err = (e, fb) => e.response?.data?.detail || e.message || fb;
const BUCKET_MEMBERS = {
  "Payment": "Payment made · Promise to pay · Arrangement request · Settlement figure request · Settlement offer / discount · Refund request",
  "Query": "Statement / document request · Figures query · Liability dispute · Account change · Complaint",
  "Legal notice": "Insolvency · Legal notice", "Other": "Internal · Needs review · Other",
};
const toEditor = (s) => ({ schedule_enabled: s?.mode === "hours", schedule_days: s?.days || [0, 1, 2, 3, 4], schedule_start: s?.start || "08:00", schedule_end: s?.end || "18:00" });
const fromEditor = (cur, patch) => {
  const v = { ...toEditor(cur), ...patch };
  return { mode: v.schedule_enabled ? "hours" : "always", days: v.schedule_days, start: v.schedule_start, end: v.schedule_end };
};

export function InboxIntelDialog({ api, settings, onChange, onClose, isAus }) {
  const [saving, setSaving] = useState(null);
  const [triageIntel, setTriageIntel] = useState(settings?.triage_intel || "");
  const [replyIntel, setReplyIntel] = useState(settings?.reply_intel || "");
  const [minConf, setMinConf] = useState(settings?.min_confidence ?? 70);
  const [invConf, setInvConf] = useState(settings?.invoice_min_confidence ?? 90);
  const [financeTo, setFinanceTo] = useState(settings?.finance_forward_to || "");
  const [invFolder, setInvFolder] = useState(settings?.invoice_folder || "");
  const autopilot = !!settings?.autopilot_available;

  const save = async (patch, label = "Saved") => {
    setSaving(label);
    try { const r = await api.setSettings(patch); onChange(r); if (label) toast.success(label); }
    catch (e) { toast.error(err(e, "Save failed")); } finally { setSaving(null); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="inbox-intel-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2"><Settings2 className="w-4 h-4" />Filters &amp; intel
            <span className="text-[11px] font-normal text-muted-foreground">{settings?.label} · {isAus ? "AUS" : "UK"}</span>
          </DialogTitle>
        </DialogHeader>
        <Tabs defaultValue={autopilot ? "autopilot" : "intel"}>
          <TabsList className="grid grid-cols-4 h-8">
            <TabsTrigger value="autopilot" className="text-xs" disabled={!autopilot} data-testid="intel-tab-autopilot">Autopilot</TabsTrigger>
            <TabsTrigger value="intel" className="text-xs" data-testid="intel-tab-intel">AI guidance</TabsTrigger>
            <TabsTrigger value="knowledge" className="text-xs" data-testid="intel-tab-knowledge">Knowledge</TabsTrigger>
            <TabsTrigger value="categories" className="text-xs" data-testid="intel-tab-categories">Triage categories</TabsTrigger>
          </TabsList>

          <TabsContent value="autopilot" className="space-y-4 pt-3">
            <div className="border rounded-md px-3 py-2 bg-slate-50/60" data-testid="intel-schedule-row">
              <ScheduleEditor value={toEditor(settings?.schedule)} tzLabel={isAus ? "Sydney time" : "UK time"} testidPrefix="inbox-intel-schedule"
                onChange={(patch) => save({ schedule: fromEditor(settings?.schedule, patch) }, "Schedule saved")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-md px-3 py-2" data-testid="intel-min-confidence-row">
                <div className="text-xs font-semibold">Minimum confidence to act</div>
                <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">Below this the email is triaged but left for a human (logged as "skipped").</p>
                <div className="flex items-center gap-2">
                  <Input type="number" min={0} max={100} value={minConf} onChange={(e) => setMinConf(e.target.value)} className="h-7 w-20 text-xs" data-testid="intel-min-confidence" />
                  <span className="text-xs text-muted-foreground">%</span>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={saving === "conf"} onClick={() => save({ min_confidence: Number(minConf) }, "Confidence threshold saved")} data-testid="intel-min-confidence-save">Save</Button>
                </div>
              </div>
              <div className="border rounded-md px-3 py-2" data-testid="intel-invoice-confidence-row">
                <div className="text-xs font-semibold">Invoice forwarding threshold</div>
                <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">Only invoices at or above this confidence are {settings?.finance_forward_to ? `forwarded to ${settings.finance_forward_to}` : settings?.invoice_folder ? `filed in the '${settings.invoice_folder}' folder` : "handled"}.</p>
                <div className="flex items-center gap-2">
                  <Input type="number" min={50} max={100} value={invConf} onChange={(e) => setInvConf(e.target.value)} className="h-7 w-20 text-xs" data-testid="intel-invoice-confidence" />
                  <span className="text-xs text-muted-foreground">%</span>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => save({ invoice_min_confidence: Number(invConf) }, "Invoice threshold saved")} data-testid="intel-invoice-confidence-save">Save</Button>
                </div>
              </div>
            </div>
            <div className="border rounded-md px-3 py-2" data-testid="intel-invoice-destination-row">
              <div className="text-xs font-semibold">Where supplier invoices go</div>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">With a finance address the invoice is forwarded there and moved to Done. Leave it empty to file invoices in a mailbox folder instead{isAus ? " (Australia keeps invoices in accounts@ for now)" : ""}.</p>
              <div className="flex flex-wrap items-center gap-2">
                <Input value={financeTo} onChange={(e) => setFinanceTo(e.target.value)} placeholder={isAus ? "finance@simplyfunded.com.au (optional)" : "finance@simplyfunded.co.uk"} className="h-7 w-64 text-xs" data-testid="intel-finance-to" />
                <span className="text-[10px] text-muted-foreground">or folder</span>
                <Input value={invFolder} onChange={(e) => setInvFolder(e.target.value)} placeholder="Invoices" className="h-7 w-36 text-xs" data-testid="intel-invoice-folder" />
                <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={saving === "inv"} onClick={() => save({ finance_forward_to: financeTo.trim(), invoice_folder: invFolder.trim() }, "Invoice destination saved")} data-testid="intel-invoice-destination-save">Save</Button>
              </div>
            </div>
            <div className="space-y-1.5" data-testid="intel-rules">
              <div className="text-xs font-semibold flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />What Autopilot is allowed to do</div>
              {Object.entries(settings?.rule_labels || {}).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-4 border rounded-md px-3 py-1.5" data-testid={`intel-rule-${k}`}>
                  <div><div className="text-xs font-medium">{v.label}</div><p className="text-[10px] text-muted-foreground">{v.detail}</p></div>
                  <Switch checked={settings?.rules?.[k] !== false} onCheckedChange={(on) => save({ rules: { [k]: on } }, `${v.label} ${on ? "on" : "off"}`)} data-testid={`intel-rule-${k}-switch`} />
                </div>
              ))}
            </div>
            <div className="border rounded-md px-3 py-2" data-testid="intel-prereply-categories">
              <div className="text-xs font-semibold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-violet-700" />Categories that get an AI pre-reply</div>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">Only for customers in arrears with a matched file. Invoices and bounces never do. In Default / Legal the draft is a holding reply only. Hover a bucket to see which categories it covers.</p>
              <div className="flex flex-wrap gap-1.5">
                {["Payment", "Query", "Legal notice", "Other"].map((c) => {
                  const on = (settings?.prereply_categories || []).includes(c);
                  return (
                    <button key={c} type="button" onClick={() => save({ prereply_categories: on ? settings.prereply_categories.filter((x) => x !== c) : [...(settings?.prereply_categories || []), c] }, `Pre-reply for ${c} ${on ? "off" : "on"}`)}
                      className={cn("rounded-full border px-2.5 py-0.5 text-[11px] transition-colors", on ? "bg-violet-700 text-white border-violet-700" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500")}
                      title={BUCKET_MEMBERS[c]} data-testid={`intel-prereply-cat-${c.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>{c}</button>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="intel" className="space-y-4 pt-3">
            <div data-testid="intel-triage-intel">
              <div className="text-xs font-semibold">Triage guidance</div>
              <p className="text-[10px] text-muted-foreground mb-1">Extra rules for the categoriser — e.g. "Swoop / broker commission chases are Other, not Invoice", "anything mentioning the ombudsman is a Query flagged for Dean".</p>
              <Textarea rows={5} value={triageIntel} onChange={(e) => setTriageIntel(e.target.value)} className="text-xs" placeholder="One rule per line…" data-testid="intel-triage-intel-text" />
            </div>
            <div data-testid="intel-reply-intel">
              <div className="text-xs font-semibold">Reply guidance (house rules for every AI draft)</div>
              <p className="text-[10px] text-muted-foreground mb-1">E.g. "never offer more than 2 weeks' grace without a plan", "always offer a call on 0203 …", "settlement figures only from the Payout letter". The AI already knows we are an unregulated commercial lender and must not cite consumer legislation.</p>
              <Textarea rows={6} value={replyIntel} onChange={(e) => setReplyIntel(e.target.value)} className="text-xs" placeholder="One rule per line…" data-testid="intel-reply-intel-text" />
            </div>
            <div className="flex justify-end">
              <Button size="sm" className="h-7 text-[11px]" disabled={!!saving} onClick={() => save({ triage_intel: triageIntel, reply_intel: replyIntel }, "AI guidance saved")} data-testid="intel-save-guidance">
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}Save guidance
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="knowledge" className="pt-3"><KnowledgePanel api={api} /></TabsContent>
          <TabsContent value="categories" className="pt-3"><CategoriesPanel api={api} /></TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function KnowledgePanel({ api }) {
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);
  const load = () => api.knowledge().then((r) => setDocs(r.docs || [])).catch((e) => toast.error(err(e, "Could not load documents")));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    for (const f of Array.from(files || [])) {
      setBusy(f.name);
      try {
        const up = await uploadInboxAttachment(api.uploadChunk, f, () => {});
        const r = await api.addKnowledge({ upload_id: up.upload_id, name: f.name, use: ["reply", "triage"] });
        toast.success(`${r.doc.name} added — ${r.doc.chars.toLocaleString("en-GB")} characters of policy text`);
      } catch (e) { toast.error(err(e, `Upload of ${f.name} failed`)); }
    }
    setBusy(null); load();
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.name}" from the AI's knowledge?`)) return;
    try { await api.deleteKnowledge(d.id); toast.success("Removed"); load(); } catch (e) { toast.error(err(e, "Delete failed")); }
  };

  return (
    <div className="space-y-3" data-testid="intel-knowledge">
      <div className="flex items-start gap-3 border rounded-md px-3 py-2 bg-indigo-50/50 border-indigo-200">
        <BookOpenText className="w-4 h-4 text-indigo-700 mt-0.5 shrink-0" />
        <p className="text-[11px] text-indigo-950">Upload the <b>Collections policy</b>, <b>Legal policy</b>, tone-of-voice notes or any house document (PDF, Word, text). Their text is given to the AI every time it categorises an email or drafts a reply, so it follows SimplyFunded's own rules instead of guessing.</p>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="intel-knowledge-upload-btn">
          {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}{busy ? `Reading ${busy}…` : "Upload document"}
        </Button>
        <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" multiple className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="intel-knowledge-file-input" />
        <span className="text-[10px] text-muted-foreground">PDF · Word (.docx) · .txt · .md — up to 60,000 characters each is used</span>
      </div>
      {docs === null ? <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>
        : docs.length === 0 ? <div className="text-xs text-muted-foreground italic" data-testid="intel-knowledge-empty">No documents yet — the AI is working from its general instructions only.</div>
        : (
          <ul className="divide-y border rounded-md">
            {docs.map((d) => (
              <li key={d.id} className="flex items-center gap-2 px-3 py-2 text-xs" data-testid={`intel-knowledge-doc-${d.id}`}>
                <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <span className="font-medium truncate flex-1">{d.name}</span>
                <span className="text-[10px] text-muted-foreground tabular-nums">{Number(d.chars).toLocaleString("en-GB")} chars</span>
                {(d.use || []).map((u) => <Badge key={u} variant="outline" className="text-[9px]">{u === "reply" ? "drafts" : "triage"}</Badge>)}
                <span className="text-[10px] text-muted-foreground">{d.uploaded_by} · {new Date(d.at).toLocaleDateString("en-GB")}</span>
                <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => remove(d)} title="Remove" data-testid={`intel-knowledge-delete-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
    </div>
  );
}

function CategoriesPanel({ api }) {
  const [cat, setCat] = useState(null);
  useEffect(() => { api.triageCatalog().then(setCat).catch((e) => toast.error(err(e, "Could not load the catalogue"))); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cat) return <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" />Loading…</div>;
  const bands = cat.arrears_bands || {};
  return (
    <div className="space-y-3" data-testid="intel-categories">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-muted-foreground">The {cat.categories.length} categories from the policy audit, the flags the AI can raise and where each email is routed. The customer file is matched <b>before</b> the AI runs so it knows the account stage.</p>
        <a href={cat.audit_doc || "/downloads/Accounts-Inbox-AI-Triage-Audit.md"} download className="inline-flex items-center gap-1 rounded-md border px-2.5 h-7 text-[11px] bg-white hover:bg-slate-50" data-testid="intel-audit-download">
          <Download className="w-3 h-3" />Download the triage audit (.md)
        </a>
      </div>
      <ul className="divide-y border rounded-md max-h-64 overflow-y-auto">
        {cat.categories.map((c) => (
          <li key={c.name} className="px-3 py-1.5 text-xs flex items-start gap-2" data-testid={`intel-category-${c.name.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
            <Badge variant="outline" className={cn("text-[9px] shrink-0 mt-0.5", CATEGORY_TONE[c.name] || CATEGORY_TONE.Other)}>{c.name}</Badge>
            <span className="text-[11px] text-slate-700 flex-1">{c.hint}</span>
            <span className="text-[10px] text-emerald-800 shrink-0" title={`Autopilot: ${c.autopilot}`}>bucket · {c.bucket}</span>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-1" data-testid="intel-flags">
        {(cat.flags || []).map((f) => <Badge key={f.name} className={cn("text-[9px] border", flagTone(f.name))} title={f.hint}>{f.name}</Badge>)}
      </div>
      <div className="border rounded-md px-3 py-2 space-y-0.5" data-testid="intel-routing">
        <div className="text-xs font-semibold">Routing hints shown in the reading pane (never enforced)</div>
        {(cat.routing || []).map((r, i) => <div key={i} className="text-[11px] text-slate-700">· {r}</div>)}
      </div>
      <div className="text-[10px] text-muted-foreground" data-testid="intel-bands">
        Stage from the file: Legal → PDN / DN issued → arrears day bands {["l1", "l2", "l3", "l4"].map((k) => `${k.toUpperCase()} ${bands[k]?.[0] ?? "?"}–${bands[k]?.[1] ?? "∞"}`).join(" · ")} days past due (placeholder bands until the Manual 6.4 levels are confirmed).
        <br />Match order: {(cat.match_order || []).join(" → ")}
      </div>
    </div>
  );
}
